import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      const dest = location.state?.from && location.state.from !== "/login"
        ? location.state.from
        : (u.role === "admin" ? "/admin" : "/portal");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16" data-testid="login-page">
      <div className="w-full max-w-md te-terminal-window">
        <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#FF3355]/80"></span>
            <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
            <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">
            traceelements --auth
          </div>
          <div className="font-mono text-[11px] text-[#FF3355]">SECURE</div>
        </div>

        <form onSubmit={submit} className="p-8 lg:p-10" data-testid="login-form">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">
            // CLIENT.PORTAL.ACCESS
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tighter">
            Authenticate.
          </h1>
          <p className="mt-3 text-sm text-[#C5C6C7]">
            Sign in to access your project dashboard, milestones, and live previews.
          </p>

          <div className="mt-8 space-y-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2 block">
                &gt; email
              </label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-4 py-3 font-mono text-sm text-[#F5F5F7] placeholder:text-[#C5C6C7]/40"
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2 block">
                &gt; password
              </label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-4 py-3 font-mono text-sm text-[#F5F5F7] placeholder:text-[#C5C6C7]/40"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="border-l-2 border-[#FF5C5C] bg-[#1F2833]/40 p-3 font-mono text-xs text-[#FF9D9D]">
                ERROR · {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors disabled:opacity-50"
            >
              {busy ? <><Loader2 size={14} className="animate-spin" /> AUTHENTICATING</> : <>[ AUTHENTICATE ] <ArrowRight size={14} /></>}
            </button>

            <div className="pt-4 border-t border-[#1F2833] text-xs text-[#C5C6C7]/70 font-mono">
              <span className="text-[#FF3355]">&gt;</span> No account?{" "}
              <Link to="/terminal" className="text-[#FF3355] hover:underline" data-testid="login-to-terminal">
                Initialize a project + create login
              </Link>{" "}
              — provisions your portal in one step.
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
